if (typeof debounce === 'undefined') {
  var debounce = function (fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function StickyHeader
 */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.headerBounds = {};
      this.header = this.querySelector('theme-header');
      this.section = this.closest('.section-header-section');
      this.inlineSearch = this.querySelector('.header-inline-search--desktop, .header-inline-search');
      this.currentScrollTop = 0;
      this.ticking = false;
      /** After reset(), layout can shift and briefly raise scrollY; ignore hide() until then to avoid a flash. */
      this._suppressHideUntil = 0;
      /** After search expands, layout/--header-height shifts can fake a scroll-down and re-collapse it. */
      this._suppressSearchHideUntil = 0;
      /** After search collapses, shorter sticky header can drop scrollY and fake a scroll-up that re-opens it. */
      this._suppressSearchShowUntil = 0;
      this._mobileNavMq = window.matchMedia('(max-width: 1067px)');
      this.onScrollHandler = this.onScroll.bind(this);
      this.onMobileNavMqChange = this.onMobileNavMqChange.bind(this);

      window.addEventListener('scroll', this.onScrollHandler, false);
      if (this._mobileNavMq.addEventListener) {
        this._mobileNavMq.addEventListener('change', this.onMobileNavMqChange);
      } else if (this._mobileNavMq.addListener) {
        this._mobileNavMq.addListener(this.onMobileNavMqChange);
      }
      /** When set (non-transparent index/product), keep section `position: sticky` at scroll top to avoid layout reflow blink. */
      this._persistentStickyShell = this.hasAttribute('data-header-sticky-shell');
      this._transparentHeader = this.hasAttribute('data-jsc-transparent-header');
      if (this._persistentStickyShell || this.isMobileNav()) {
        this.section.classList.add('section-header-section--is-sticky');
      }
      if (this._transparentHeader) {
        this.updateTransparentAtTop(window.scrollY);
      }
      this.createObserver();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      if (this._mobileNavMq) {
        if (this._mobileNavMq.removeEventListener) {
          this._mobileNavMq.removeEventListener('change', this.onMobileNavMqChange);
        } else if (this._mobileNavMq.removeListener) {
          this._mobileNavMq.removeListener(this.onMobileNavMqChange);
        }
      }
    }
    isMobileNav() {
      return this._mobileNavMq ? this._mobileNavMq.matches : window.matchMedia('(max-width: 1067px)').matches;
    }
    onMobileNavMqChange() {
      if (!this.isMobileNav()) {
        this.setSearchScrollHidden(false);
        if (!this._persistentStickyShell) {
          this.section.classList.remove('section-header-section--is-sticky');
        }
      } else {
        this.section.classList.remove('section-header-section--is-hidden');
        this.section.classList.add('section-header-section--is-sticky');
        this.setSearchScrollHidden(false);
      }
    }
    createObserver() {
      new IntersectionObserver((entries, observer) => {
        const entry = entries[0];
        this.headerBounds = entry.intersectionRect;

        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.scrollY;
          const boundingClientRect = entry.boundingClientRect;

          this.headerBounds = {
            top: scrollTop + boundingClientRect.top,
            bottom: scrollTop + boundingClientRect.bottom,
          };
        }

        observer.disconnect();
        if (this._transparentHeader) {
          this.updateTransparentAtTop(window.scrollY);
        }
      }).observe(this.header);
    }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const delta = scrollTop - this.currentScrollTop;
        const pastHeader = scrollTop > this.headerBounds.bottom;
        const atOrAboveHeaderOrigin = scrollTop <= this.headerBounds.top;

        if (this.isMobileNav()) {
          // Asymmetric thresholds: require a clearer upward move to re-open (scroll-down jitter is common).
          this.onMobileScroll(scrollTop, delta > 10, delta < -20, atOrAboveHeaderOrigin);
        } else if (scrollTop > this.currentScrollTop && pastHeader) {
          const postResetJitter =
            this._suppressHideUntil &&
            performance.now() < this._suppressHideUntil &&
            scrollTop < this.headerBounds.bottom + 96;
          if (!postResetJitter) {
            this.hide();
          }
        } else if (scrollTop < this.currentScrollTop && pastHeader) {
          this.show();
        } else if (atOrAboveHeaderOrigin) {
          this.reset();
        }
        if (this._transparentHeader) {
          this.updateTransparentAtTop(scrollTop);
        }
        // Prefer live scrollY so layout shifts from search expand/collapse do not invent a fake delta next frame.
        this.currentScrollTop = window.scrollY;
        this.ticking = false;
      });
    }

    /** Mobile: keep menu chrome sticky/visible; only collapse the search row on scroll down. */
    onMobileScroll(scrollTop, scrollingDown, scrollingUp, atOrAboveHeaderOrigin) {
      this.section.classList.add('section-header-section--is-sticky');
      this.section.classList.remove('section-header-section--is-hidden');

      // Collapse as soon as the user leaves the top — do not wait for full headerBounds.bottom.
      if (atOrAboveHeaderOrigin || scrollTop <= 8) {
        this._suppressSearchShowUntil = 0;
        this.setSearchScrollHidden(false);
        this.header.classList.remove('is-sticky');
        return;
      }

      this.header.classList.add('is-sticky');

      if (scrollingDown) {
        if (
          performance.now() < this._suppressSearchHideUntil ||
          this.isSearchInteractionActive()
        ) {
          return;
        }
        this.setSearchScrollHidden(true);
      } else if (scrollingUp) {
        if (performance.now() < this._suppressSearchShowUntil) {
          return;
        }
        this.setSearchScrollHidden(false);
      }
    }

    isSearchInteractionActive() {
      const search = this.inlineSearch;
      if (!search) return false;
      if (search.classList.contains('is-active')) return true;
      const active = document.activeElement;
      return Boolean(active && search.contains(active));
    }

    setSearchScrollHidden(hidden) {
      const search = this.inlineSearch;
      if (!search) return;
      const wasHidden = search.classList.contains('jsc-header-search--scroll-hidden');
      if (hidden === wasHidden) return;
      search.classList.toggle('jsc-header-search--scroll-hidden', hidden);

      // Height changes from expand/collapse shift scrollY and can flip direction for one frame.
      const now = performance.now();
      if (hidden) {
        this._suppressSearchShowUntil = now + 400;
      } else {
        this._suppressSearchHideUntil = now + 350;
      }

      const syncAfterToggle = () => {
        this.currentScrollTop = window.scrollY;
        if (typeof this.header?.setHeaderHeight === 'function') {
          this.header.setHeaderHeight();
        }
      };
      syncAfterToggle();

      const onTransitionEnd = (event) => {
        if (event.target !== search) return;
        if (event.propertyName && event.propertyName !== 'max-height') return;
        search.removeEventListener('transitionend', onTransitionEnd);
        syncAfterToggle();
      };
      search.addEventListener('transitionend', onTransitionEnd);
      setTimeout(() => {
        search.removeEventListener('transitionend', onTransitionEnd);
        syncAfterToggle();
      }, 320);
    }

    hide() {
      this.section.classList.add(
        'section-header-section--is-hidden',
        'section-header-section--is-sticky'
      );
      this.header.classList.remove('is-sticky');
    }

    show() {
      this.section.classList.add('section-header-section--is-sticky', 'section-header-section--animate');
      this.section.classList.remove('section-header-section--is-hidden');
      this.header.classList.add('is-sticky');
    }

    reset() {
      this.section.classList.remove(
        'section-header-section--is-hidden',
        'section-header-section--animate'
      );
      if (!this._persistentStickyShell) {
        this.section.classList.remove('section-header-section--is-sticky');
      }
      this.header.classList.remove('is-sticky');
      this._suppressHideUntil = performance.now() + 100;
    }

    updateTransparentAtTop(scrollTop) {
      const top = typeof this.headerBounds.top === 'number' ? this.headerBounds.top : 0;
      const atTop = scrollTop <= top;
      this.section.classList.toggle('section-header-section--jsc-at-top', atTop);
    }
  }
  customElements.define('sticky-header', StickyHeader);
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.section-header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this.search_toggles = document.querySelectorAll('.thb-quick-search');
      this.search_drawer = document.getElementById('Search-Drawer');
      this.search_input = this.search_drawer ? this.search_drawer.querySelector('input[type="search"]') : null;
      const body = document.body;
      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            if (typeof window.jscCloseDesktopMegaMenu === 'function') {
              window.jscCloseDesktopMegaMenu();
            }
            if (this.toggle && this.toggle.hasAttribute('open')) {
              body.classList.remove('overflow-hidden');
            }
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
            this.querySelectorAll('details').forEach((detailsElement) => {
              this.closeSubmenu(detailsElement);
            });
          }
        }
      });

      // Search toggle
      this.search_toggles.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();

          body.classList.add('open-cc', 'overflow-hidden');
          this.search_drawer.classList.toggle('active');
          setTimeout(() => {
            if (this.search_input) this.search_input.focus({ preventScroll: true });
          }, 100);
          dispatchCustomEvent('search:open');
          this.search_drawer.removeAttribute('inert');
        });
      });
      // Mobile menu toggles
      const toggleClass = this.toggle.classList;
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (toggleClass.contains('active')) {
          e.preventDefault();
          body.classList.remove('overflow-hidden');
          toggleClass.remove('active');
          this.closeAnimation(this.toggle);
          setTimeout(() => {
            this.closeAll();
          }, 250);
        } else {
          body.classList.add('overflow-hidden');
          setTimeout(() => {
            toggleClass.add('active');
          });
        }
        window.dispatchEvent(new Event('resize-select'));
      });

      // Mobile Menu offset + height — single scroll handler
      this.onScrollResize = () => { this.setHeaderOffset(); this.setHeaderHeight(); };
      window.addEventListener('scroll', this.onScrollResize, { passive: true });
      window.addEventListener('resize', debounce(this.onScrollResize, 100));

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.header-announcement-bar')) {

        window.addEventListener('scroll', this.setAnnouncementHeight.bind(this), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Mobile Menu Buttons.
      if (this.menu) {
        this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
        this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

        const closeMobileDrawer = () => {
          body.classList.remove('overflow-hidden');
          if (this.toggle) {
            this.toggle.classList.remove('active');
            this.toggle.removeAttribute('open');
          }
          this.closeAll();
        };

        this.menu.querySelectorAll('.mobile-menu-close-trigger').forEach((btn) => {
          btn.addEventListener('click', closeMobileDrawer);
        });

        this.menu.addEventListener('click', (e) => {
          if (e.target.closest('[data-jsc-mobile-child-placeholder]')) {
            e.preventDefault();
          }
          const util = e.target.closest('.jsc-mobile-menu__utility');
          if (util && util.getAttribute('href') === '#') {
            e.preventDefault();
          }
        });

        const mobileCart = this.menu.querySelector('.jsc-mobile-menu__cart');
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (mobileCart && cartDrawerEl && typeof cartDrawerEl.open === 'function') {
          mobileCart.addEventListener('click', (e) => {
            e.preventDefault();
            cartDrawerEl.open();
          });
        }
      }
    }

    setAnnouncementHeight() {
      const a_bar = document.querySelector('.header-announcement-bar');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    setHeaderOffset() {
      let h = window.innerWidth > 768 ? this.header_section.getBoundingClientRect().top : 0;

      if (h >= 0) {
        document.documentElement.style.setProperty('--header-offset', h + 'px');
      }
    }
    setHeaderHeight() {
      const r = this.getBoundingClientRect();
      let h = Math.round(r.height);
      if (h < 1 && this.header_section) {
        h = Math.round(this.header_section.getBoundingClientRect().height);
      }
      if (h < 1) {
        h = 64;
      }
      document.documentElement.style.setProperty('--header-height', h + 'px');
      document.documentElement.style.setProperty('--header-anchor-bottom', Math.max(0, Math.round(r.bottom)) + 'px');
    }
    closeAll() {
      this.querySelectorAll('details').forEach((detailsElement) => {
        this.closeSubmenu(detailsElement);
      });
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        if (parentMenuElement) parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      setTimeout(() => detailsElement.removeAttribute('open'), 400);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header = this.closest('.header');
      this.all_subs = this.querySelectorAll('.menu-item-has-children');
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      window.addEventListener('resize', debounce(function () {
        _this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(function () {
        _this.resizeSubMenus();
      });

      this.all_subs.forEach((subs) => {
        subs.addEventListener('mouseenter', () => {
          this.header.classList.add('menu-item-hover');
        });
        subs.addEventListener('mouseleave', () => {
          this.header.classList.remove('menu-item-hover');
        });
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}